/* eslint import/namespace: [2, { allowComputed: true }] */
import * as d3 from 'd3';
import _ from 'lodash';
import unitHelper from '@client/helper/unit';
import {
  CHART_TYPE_POLYLINE,
  CHART_TYPE_POINT,
  CHART_TYPE_HISTOGRAM,
  CHART_TYPE_FILL_AREA,
  CHART_TYPE_MULTI_COLOR_FILL_AREA,
  CHART_TYPE_AREA
} from '@client/pages/activity-detail/config/chart.types';

export class Chart {
  STROKE_DASHARRAY = '3,3';

  CHART_DURATION = 1000;

  constructor(options) {
    const that = this;
    that.el = options.el || null;

    // 主题
    that.theme = options.theme || 'theme-dark';

    that.margin = options.margin || null;
    that.width = that.el?.clientWidth || 0;
    that.height = that.el?.clientHeight || 0;
    this.recommandLabel = options.recommandLabel;

    // 设置一些图表元素的显示属性
    that.setElementStatus(options);

    // 整个图表
    that.svg = d3.select(that.el).append('svg').attr('width', that.width).attr('height', that.height);

    // 图表内容区域（不含坐标轴）
    that.content = that.svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    that.contentWidth = that.width - that.margin.left - that.margin.right;
    that.contentHeight = that.height - that.margin.top - that.margin.bottom;

    that.xScale = null;
    that.xAxisKey = options.xAxisKey;

    // 用于十字线移动时回调（获取鼠标对应位置的数据）
    that.crossHairCallback = options.crossHairCallback || null;

    // x轴刻度
    that.xTicks = options.xTicks || [];
    that.xTickInfo =
      options.xTickInfo ||
      ((value) => {
        return {
          text: value,
          value
        };
      });

    that.yScales = {};
    that.showingAttributes = [];

    that.data = null;

    // 使用初始数据进行初始化
    that.setData(that.data);
    that.rootListener();
  }

  // 设置一些图表元素的显示属性
  setElementStatus(options = {}) {
    const that = this;

    const setBoolean = (val, defaultVal = false) => {
      return [true, false].includes(val) ? val : defaultVal;
    };

    // x轴刻度文字大小
    that.xTickFontSize = options.xTickFontSize >= 0 ? options.xTickFontSize : 12;
    // x轴刻度线长度
    that.xTickLineSize = options.xTickLineSize >= 0 ? options.xTickLineSize : 5;
    // 是否绘制x轴刻度线（默认绘制）
    that.showXShortTicks = setBoolean(options.showXShortTicks, true);
    // 是否绘制x轴刻度文字（默认绘制）
    that.showXTickTexts = setBoolean(options.showXTickTexts, true);
    // 是否绘制x轴等分线（默认绘制）
    that.showXUniformityLine = setBoolean(options.showXUniformityLine, true);

    // 是否绘制y轴线（默认绘制）
    that.showYLine = setBoolean(options.showYLine, true);
    // 是否绘制y轴刻度的横虚线（默认绘制）
    that.showYTicks = setBoolean(options.showYTicks, true);
    // 是否绘制y轴刻度的文字（默认绘制）
    that.showYTickTexts = setBoolean(options.showYTickTexts, true);

    // 鼠标经过时是否显示属性窗口
    that.showAttrTooltip = setBoolean(options.showAttrTooltip, true);
    // 鼠标经过时是否显示十字线的竖线
    that.showCrossHairVerticalLine = setBoolean(options.showCrossHairVerticalLine, true);
    // 鼠标经过时是否显示十字线的横线
    that.showCrossHairHorizontalLine = setBoolean(options.showCrossHairHorizontalLine, false);

    // 昂柱状图点击时触发的方法
    that.onRectClick = options.onRectClick || null;
  }

  // 监听图表根元素事件
  rootListener() {
    const that = this;
    // 十字线
    d3.select(that.el)
      .on('mousemove', (e) => {
        requestAnimationFrame(() => {
          that.drawCrossHair(e);
        });
      })
      .on('mouseleave', (e) => {
        requestAnimationFrame(() => {
          that.deleteCrossHair();
        });
      })
      .on('touchstart', (e) => {
        e.preventDefault();
        requestAnimationFrame(() => {
          that.drawCrossHair(e?.touches?.[0]);
        });
      })
      .on('touchmove', (e) => {
        e.preventDefault();
        requestAnimationFrame(() => {
          that.drawCrossHair(e?.touches?.[0]);
        });
      });
  }

  /**
   * 主题颜色
   * @returns {*}
   */
  get themeStyle() {
    const that = this;
    const message = {
      'theme-dark': {
        xTooltipBg: '#3B3C44',
        xTooltipColor: '#ffffff',
        xScaleLineColor: 'rgba(222, 223, 225, 0.1)',
        xTickLineColor: '#999999',
        xTickTextColor: '#ffffff',

        yScaleLineColor: '#999999',
        yScaleUnitTextColor: '#999999',
        yTickTextColor: '#999999',
        yTickLineColor: 'rgba(222, 223, 225, 0.1)',

        // 属性窗口
        attrTooltipBg: '#2e3851',
        attrTooltipTitleColor: 'rgba(255, 255, 255, 0.6)',
        attrItemTextColor: '#ffffff',

        // 平均值标识
        avgLineColor: '#ff7c7c',
        avgTooltipBg: '#ffffff',
        avgTooltipColor: '#2e2e2e',

        // 最大值标识
        maxTooltipBg: '#ffffff',
        maxTooltipColor: '#2e2e2e',

        // 最小值标识
        minTooltipBg: '#ffffff',
        minTooltipColor: '#2e2e2e',

        crossHairHorizontalLineColor: '#999999',
        crossHairVerticalLineColor: '#999999',

        openZoneLeftEdgeColor: '#ffffff',
        openZoneRightEdgeColor: '#ffffff'
      }
    };
    const themeNames = Object.keys(message);
    if (themeNames.includes(that.theme)) {
      return message[that.theme];
    }
    return message[themeNames[0]];
  }

  // 取样之后的数据
  get chartData() {
    return this.data;
  }

  remove() {
    this.svg.remove();
  }

  clean() {
    // this.content
  }

  // 判断是否为不存在的元素
  isEmptySelect(select) {
    return [undefined, null].includes(select._groups[0][0]);
  }

  // 将颜色转换为rgba格式，change为变色方式
  // 使用颜色前统一转换为rgba格式，解决出现一瞬间蓝色的问题
  getFillColor(color, opacity) {
    const activeColor = d3.color(color);
    if (!activeColor) {
      return color;
    }
    if (opacity) {
      activeColor.opacity = opacity;
    }
    for (const n in activeColor) {
      if (isNaN(activeColor[n])) {
        activeColor[n] = 0;
      }
    }
    return `rgba(${activeColor.r}, ${activeColor.g}, ${activeColor.b}, ${activeColor.opacity})`;
  }

  // 设置数据
  setData(data) {
    if (!data) {
      return;
    }
    const that = this;
    that.data = data;
  }

  // 修正超出y坐标的数据为正常范围值
  valueCorrect(attr, value) {
    // 若该属性有自己的数据修正逻辑，则优先使用
    if (attr.valueCorrect && attr.valueCorrect instanceof Function) {
      return attr.valueCorrect(attr, value);
    }
    const that = this;
    const chartName = attr.chartName;
    const bottomY = that.yScales[chartName].domain()[0];
    const topY = that.yScales[chartName].domain()[1];
    let result = value;

    // 正向y轴
    if (bottomY < topY) {
      if (result < bottomY) {
        result = bottomY;
      } else if (topY < result) {
        result = topY;
      }
    }

    // 反向y轴
    if (bottomY > topY) {
      if (result > bottomY) {
        result = bottomY;
      } else if (topY > result) {
        result = topY;
      }
    }

    return result;
  }

  // 创建x轴比例尺
  createXAxis(domain, scaleType) {
    const that = this;
    that.xScale = d3[scaleType || 'scaleLinear']().domain(domain).range([0, that.contentWidth]);
  }

  /**
   * 设置x轴刻度文字大小
   * @param fontSize
   */
  setXTickFontSize(fontSize) {
    if (typeof fontSize !== 'number' || fontSize < 0) {
      return;
    }
    this.xTickFontSize = fontSize;
    this.drawXTicks();
    this.drawXTickTexts();
  }

  /**
   * 设置x轴刻度线长度
   * @param lineSize
   */
  setXTickLineSize(lineSize) {
    if (typeof lineSize !== 'number' || lineSize < 0) {
      return;
    }
    this.xTickLineSize = lineSize;
    this.drawXTicks();
    this.drawXTickTexts();
  }

  // 创建x轴等分线
  drawXUniformityLine() {
    const that = this;
    const xAxis = that.svg.select('.x-axis');
    let xAxisTicks = that.svg.select('.x-axis-uniformity-line');

    if (that.isEmptySelect(xAxis)) {
      return;
    }

    // 定义直线画法
    const lineFun = d3
      .line()
      .defined((d, i) => {
        // 控制是否绘制该点（return false不绘制）
        const value = d.x;
        return !(isNaN(value) || [undefined, ''].includes(value));
      })
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    // 生成X轴刻度
    xAxisTicks.remove();
    xAxisTicks = xAxis.append('g').attr('class', 'x-axis-uniformity-line');

    const domain = that.xScale.domain();
    const part = 1;
    const gap = (domain[1] - domain[0]) / part;

    for (let n = 0; n <= part; n++) {
      const x = domain[0] + gap * n;
      xAxisTicks
        .append('path')
        .attr('d', () => {
          return lineFun([
            { x: that.xScale(x), y: 0 },
            { x: that.xScale(x), y: -that.contentHeight }
          ]);
        })
        .attr('fill', 'none')
        .attr('stroke-width', '1')
        .attr('stroke', that.getFillColor(that.themeStyle.xScaleLineColor))
        .attr('stroke-dasharray', that.STROKE_DASHARRAY);
    }
  }

  // 创建x轴刻度线
  drawXTicks() {
    const that = this;
    const xAxis = that.svg.select('.x-axis');
    let xAxisTicks = that.svg.select('.x-axis-ticks');

    if (that.isEmptySelect(xAxis)) {
      return;
    }

    // 定义直线画法
    const lineFun = d3
      .line()
      .defined((d, i) => {
        // 控制是否绘制该点（return false不绘制）
        const value = d.x;
        return !(isNaN(value) || [undefined, ''].includes(value));
      })
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    // 生成X轴刻度
    xAxisTicks.remove();
    xAxisTicks = xAxis.append('g').attr('class', 'x-axis-ticks');
    xAxisTicks
      .selectAll('.x-tick')
      .data(that.xTicks)
      .enter()
      .append('path')
      .attr('class', 'x-tick')
      .attr('d', (item) => {
        return lineFun([
          { x: item.x, y: 0 },
          { x: item.x, y: that.xTickLineSize }
        ]);
      })
      .attr('fill', 'none')
      .attr('stroke-width', '1')
      .attr('stroke', that.themeStyle.xTickLineColor);
  }

  /**
   * 生成X轴刻度文字
   */
  drawXTickTexts() {
    const that = this;
    const xAxis = that.svg.select('.x-axis');
    let xAxisTickTexts = that.svg.select('.x-axis-tick-texts');

    if (that.isEmptySelect(xAxis)) {
      return;
    }

    xAxisTickTexts.remove();
    xAxisTickTexts = xAxis.append('g').attr('class', 'x-axis-tick-texts');
    xAxisTickTexts
      .selectAll('.x-tick-text')
      .data(that.xTicks)
      .enter()
      .append('text')
      .attr('class', 'x-tick-text')
      .attr('x', (item) => {
        return item.x;
      })
      .attr('y', that.xTickLineSize + 3)
      .attr('fill', that.themeStyle.xTickTextColor)
      .attr('font-size', `${that.xTickFontSize}px`)
      .attr('dominant-baseline', 'text-before-edge')
      .attr('text-anchor', 'middle')
      .text((item) => {
        return item.xTickText || item.text;
      });
  }

  // 画出x轴
  drawXScale(ticks = [], min, max, scaleType) {
    const that = this;

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    if (!that.xScale) {
      console.error(new Error('绘制x轴需要先创建x轴比例尺'));
      return;
    }

    let xAxis = that.svg.select('.x-axis');
    // 没有创建过x轴元素
    xAxis.remove();
    // 绘制x轴横线
    xAxis = that.svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(${that.margin.left}, ${that.margin.top + that.contentHeight})`);

    xAxis
      .append('path')
      .attr('class', 'x-axis-line')
      .attr(
        'd',
        lineFun([
          { x: 0, y: 0 },
          { x: that.contentWidth, y: 0 }
        ])
      )
      .attr('fill', 'none')
      .attr('stroke-width', 1)
      .attr('stroke', that.getFillColor(that.themeStyle.xScaleLineColor))
      .attr('stroke-dasharray', that.STROKE_DASHARRAY);

    that.xTicks = ticks.map((item, index) => {
      const newItem = that.xTickInfo(item);
      newItem.x = that.xScale(newItem.value);
      return newItem;
    });

    // 生成X轴短刻度
    if (that.showXShortTicks) {
      that.drawXTicks();
    }

    // 生成X轴刻度文字
    if (that.showXTickTexts) {
      that.drawXTickTexts();
    }

    // 生成x轴等分线
    if (that.showXUniformityLine) {
      that.drawXUniformityLine();
    }
  }

  // 使用比例尺反向获取该坐标对应的值
  getValueFromXScale(x) {
    return Math.round(this.xScale.invert(x));
    // return Math.floor(this.xScale.invert(x))
  }

  getDataValue(attr, d, index, args) {
    let result = '';

    if (attr.valueKey) {
      result = d[attr.valueKey];
    }

    if (attr.valueFunction) {
      result = attr.valueFunction(d, index, args);
    }

    return result;
  }

  // 创建y轴比例尺
  createYAxis(attr) {
    const that = this;
    const chartName = attr.chartName;
    const scaleType = attr.scaleType;
    const scaleDomain = attr.scaleDomain;
    const scaleRange = attr.scaleRange;
    const data = that.data.map((d, index) => {
      return that.getDataValue(attr, d, index);
    });
    let min = Reflect.has(attr, 'min') ? attr.min : d3.min(data) * 0.9;
    let max = Reflect.has(attr, 'max') ? attr.max : d3.max(data) * 1.1;
    min = [undefined].includes(min) ? 0 : min;
    max = [undefined].includes(max) ? (min + 1) * 2 : max;
    if (min === max) {
      max = (min + 1) * 2;
    }

    that.yScales[chartName] = d3[scaleType || 'scaleLinear']()
      .domain(scaleDomain || [min, max])
      // .domain([max, min])
      .range(scaleRange || [0, that.contentHeight]);
  }

  // 画出y轴
  drawYScale(attrs = [], scaleType) {
    const that = this;
    let yAxis = that.svg.select('.y-axis');
    const showYLine = that.showYLine;
    const showYTicks = that.showYTicks;
    const fontSize = 10;
    const textWidth = 40;
    const textGap = 5;
    const duration = 1000;

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    if (that.isEmptySelect(yAxis)) {
      yAxis = that.svg.append('g').attr('class', 'y-axis').attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    }

    attrs.forEach((attr, index) => {
      that.createYAxis(attr);
    });

    attrs
      .filter((attr, index) => {
        return !attr.noYScale;
      })
      .forEach((attr, index) => {
        const axisName = `${attr.chartName}-axis`;
        const isLeft = index % 2 === 0 ? -1 : 1;
        const x = that.contentWidth * (index % 2) + isLeft * textWidth * Math.floor(index / 2);

        // 如果没有分组，创建分组
        let yGroup = yAxis.select(`.${axisName}`);
        if (that.isEmptySelect(yGroup)) {
          yGroup = yAxis.append('g').attr('class', axisName).attr('transform', `translate(0, 0)`);
        }

        // 生成y轴线
        if (showYLine) {
          let yLine = yGroup.select(`.line`);
          if (that.isEmptySelect(yLine)) {
            yLine = yGroup
              .append('path')
              .attr('class', 'y-axis-line')
              .attr(
                'd',
                lineFun([
                  { x: 0, y: 0 },
                  { x: 0, y: that.contentHeight }
                ])
              )
              .attr('fill', 'none')
              .attr('stroke-width', 1)
              .attr('stroke', that.themeStyle.yScaleLineColor);
            // .attr('stroke-dasharray', '5,5')
          } else {
            yLine
              .transition()
              .duration(duration)
              .attr(
                'd',
                lineFun([
                  { x, y: 0 },
                  { x, y: that.contentHeight }
                ])
              );
          }
        }

        // 生成y轴刻度
        if (showYTicks && attr.ticks && attr.ticks.length > 0) {
          let yTicks = yGroup.select(`.ticks`);
          // y轴刻度的公共属性设置
          const setTickAttr = (ticks) => {
            ticks
              .attr('class', 'tick')
              .attr('d', (item) => {
                // 长刻度线
                return lineFun([
                  {
                    x: 0,
                    y: that.contentHeight - that.yScales[attr.chartName](item.value)
                  },
                  {
                    x: that.contentWidth,
                    y: that.contentHeight - that.yScales[attr.chartName](item.value)
                  }
                ]);
              })
              .attr('fill', 'none')
              .attr('stroke-width', 1)
              .attr('stroke', that.getFillColor(that.themeStyle.yTickLineColor))
              .attr('stroke-dasharray', that.STROKE_DASHARRAY);
          };

          if (that.isEmptySelect(yTicks)) {
            yTicks = yGroup.append('g').attr('class', `ticks`);

            setTickAttr(yTicks.selectAll('.tick').data(attr.ticks).enter().append('path'));
          } else {
            // 过渡刻度
            const transitionTicks = () => {
              const update = yTicks.selectAll(`.ticks`).data(attr.ticks);
              const enter = update.enter();
              const exit = update.exit();

              exit.remove();

              setTickAttr(update.transition().duration(duration));

              setTickAttr(enter.append('path').attr('class', 'x-tick'));
            };
            transitionTicks();
          }
        }

        // 生成y轴刻度文字
        if (that.showYTickTexts) {
          let yTickTexts = yGroup.select(`.tick-texts`);
          // y轴刻度文字的公共属性设置
          const setTickTextAttr = (tickText) => {
            tickText
              .attr('class', 'tick-text')
              .attr('x', (d, i) => {
                return {
                  0: -textWidth,
                  1: that.contentWidth,
                  2: -2 * textWidth - textGap,
                  3: that.contentWidth + textWidth + textGap
                }[index];
              })
              .attr('y', (d) => {
                return that.contentHeight - that.yScales[attr.chartName](d.value) - fontSize * 0.8;
              })
              .attr('width', textWidth)
              .attr('height', fontSize * 1.2);

            tickText.html((d, i) => {
              return `<div style="color: ${attr.color}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: ${
                fontSize + 'px'
              }; text-align: ${isLeft === -1 ? 'right' : 'left'};">${d.text}</div>`;
            });
          };

          if (attr.ticks && attr.ticks.length > 0) {
            if (that.isEmptySelect(yTickTexts)) {
              yTickTexts = yGroup.append('g').attr('class', `tick-texts`);

              setTickTextAttr(yTickTexts.selectAll('.tick-text').data(attr.ticks).enter().append('foreignObject'));
            } else {
              // 过渡刻度文字
              const transitionTickTexts = () => {
                const update = yTickTexts.selectAll(`.tick-texts`).data(attr.ticks);
                const enter = update.enter();
                const exit = update.exit();

                exit.remove();

                setTickTextAttr(update.transition().duration(1000));

                setTickTextAttr(enter.append('foreignObject').attr('class', 'tick-text'));
              };
              transitionTickTexts();
            }

            // 生成y轴单位文字
            const yTickUnit = yTickTexts.select(`.tick-unit`);
            yTickUnit.remove();
            // setTickUnitAttr(yTickTexts.append('text'))
          }
        }

        // 生成平均值标识
        if (attr.drawAvgLine) {
          that.drawAvgTip(attr, yGroup);
        }

        // 最大值标识
        if (attr.drawMaxValueTips) {
          that.drawMaxValueTip(attr, yGroup);
        }

        // 最小值标识
        if (attr.drawMinValueTips) {
          that.drawMinValueTip(attr, yGroup);
        }
      });
  }

  // 画出平均值标识
  drawAvgTip(attr, yGroup) {
    const that = this;
    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    if (
      !Object.prototype.hasOwnProperty.call(attr, 'avg') ||
      ['', null, undefined, 'undefined'].includes(attr.avg) ||
      !yGroup ||
      that.isEmptySelect(yGroup)
    ) {
      return;
    }

    const startGap = 0;
    const fontSize = 10;
    const avgTip = yGroup.append('g').attr('class', 'avg-tip');
    const avgTipY = that.contentHeight - that.yScales[attr.chartName](attr.avg);
    const text = attr.tickText ? attr.tickText(attr.avg) : attr.avg;
    const textEleSize = that.getTextEleSize(text, fontSize);

    const rectLeftRightPadding = 0;
    const rectWidth = textEleSize.width + rectLeftRightPadding * 2;
    const rectHeight = textEleSize.height + 2;

    const arrowWidth = 5;

    // 生成线条
    avgTip
      .append('path')
      .attr(
        'd',
        lineFun([
          { x: 0, y: avgTipY },
          { x: that.contentWidth, y: avgTipY }
        ])
      )
      .attr('fill', 'none')
      .attr('stroke-width', 1)
      .attr('stroke', that.themeStyle.avgLineColor)
      .attr('stroke-dasharray', that.STROKE_DASHARRAY);

    // 指示标签
    avgTip
      .append('polygon')
      .attr(
        'points',
        `0,0
        ${rectWidth},0
        ${rectWidth + arrowWidth},${rectHeight / 2}
        ${rectWidth},${rectHeight}
        0,${rectHeight}
        0,0`
      )
      .attr('fill', that.themeStyle.avgTooltipBg)
      .attr('transform', `translate(${-rectWidth - arrowWidth}, ${avgTipY - rectHeight / 2})`);

    // 平均值文本
    avgTip
      .append('text')
      .text(text)
      .attr('fill', that.themeStyle.avgTooltipColor)
      .attr('font-size', `${fontSize}px`)
      .attr('dominant-baseline', 'central')
      .attr('text-anchor', 'end')
      .attr('x', -startGap - arrowWidth - rectLeftRightPadding)
      .attr('y', avgTipY);
  }

  // 最大值标识
  drawMaxValueTip(attr, yGroup) {
    const that = this;
    const fontSize = 12;

    if (!(attr && attr.maxTips && attr.maxTips.xValue && attr.maxTips.yValue) || that.isEmptySelect(yGroup)) {
      return;
    }

    const yValue = that.valueCorrect(attr, attr.maxTips.yValue);

    const maxTip = yGroup.append('g').attr('class', 'max-tip');
    const maxTipX = that.xScale(attr.maxTips.xValue);
    const maxTipY = that.contentHeight - that.yScales[attr.chartName](yValue);
    const maxText = attr.tickText ? attr.tickText(yValue) : yValue;
    const textEleSize = that.getTextEleSize(maxText, fontSize);

    const rectTopBottomPadding = 5;
    const rectLeftRightPadding = 8;
    const rectWidth = textEleSize.width + rectLeftRightPadding * 2;
    const rectHeight = textEleSize.height + rectTopBottomPadding * 2;

    // 指示标签
    maxTip
      .append('rect')
      .attr('x', maxTipX - rectWidth / 2)
      .attr('y', maxTipY - rectHeight * 1.5)
      .attr('width', rectWidth)
      .attr('height', rectHeight)
      .attr('rx', 2)
      .attr('ry', 2)
      .attr('fill', that.themeStyle.maxTooltipBg);

    // 最大值文本
    maxTip
      .append('text')
      .text(maxText)
      .attr('fill', that.themeStyle.maxTooltipColor)
      .attr('font-size', `${fontSize}px`)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('x', maxTipX)
      .attr('y', maxTipY - rectHeight);
  }

  // 最小值标识
  drawMinValueTip(attr, yGroup) {
    const that = this;
    const fontSize = 12;

    if (!(attr && attr.minTips && attr.minTips.xValue && attr.minTips.yValue) || that.isEmptySelect(yGroup)) {
      return;
    }

    const yValue = that.valueCorrect(attr, attr.minTips.yValue);

    const minTip = yGroup.append('g').attr('class', 'min-tip');
    const minTipX = that.xScale(attr.minTips.xValue);
    const minTipY = that.contentHeight - that.yScales[attr.chartName](yValue);
    const minText = attr.tickText ? attr.tickText(yValue) : yValue;
    const textEleSize = that.getTextEleSize(minText, fontSize);

    const rectTopBottomPadding = 5;
    const rectLeftRightPadding = 8;
    const rectWidth = textEleSize.width + rectLeftRightPadding * 2;
    const rectHeight = textEleSize.height + rectTopBottomPadding * 2;

    // 指示标签
    minTip
      .append('rect')
      .attr('x', minTipX - rectWidth / 2)
      .attr('y', minTipY + rectHeight * 0.5)
      .attr('width', rectWidth)
      .attr('height', rectHeight)
      .attr('rx', 2)
      .attr('ry', 2)
      .attr('fill', that.themeStyle.minTooltipBg);

    // 平均值文本
    minTip
      .append('text')
      .text(minText)
      .attr('fill', that.themeStyle.minTooltipColor)
      .attr('font-size', `${fontSize}px`)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('x', minTipX)
      .attr('y', minTipY + rectHeight);
  }

  // 统一属性分组命名格式
  attrGroupClassName(attr) {
    return `attribute-${attr.chartName}-chart`;
  }

  // 将颜色字符串转换为渐变数组
  colorStrToGradientArray(color) {
    if (typeof color === 'string') {
      const colorObj = d3.color(color);
      return [
        {
          offset: '0%',
          color: `rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, 1)`
        },
        {
          offset: '100%',
          color: `rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, 0)`
        }
      ];
    }
    return color;
  }

  // 创建渐变色块
  createLinearGradient(className, color, gradientUnits = 'userSpaceOnUse') {
    const that = this;
    const linerGradientId = `${className}-color-${new Date().getTime()}`;
    let linerGradient = that.content.select(`#${linerGradientId}`);
    linerGradient.remove();
    linerGradient = that.content
      .append('linearGradient')
      .attr('id', linerGradientId)
      .attr('gradientUnits', gradientUnits)
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', that.contentHeight);

    color = color || 'rgba(0, 0, 0, 0)';
    if (Array.isArray(color) && color.length > 0) {
      color.forEach((item) => {
        linerGradient.append('stop').attr('offset', item.offset).style('stop-color', item.color);
      });
    } else {
      linerGradient.append('stop').attr('offset', '0%').style('stop-color', color);

      linerGradient.append('stop').attr('offset', '100%').style('stop-color', color);
    }
    return linerGradientId;
  }

  // 折线图出现动画
  polylineChartAnimation(line) {
    return line;
    // .style('stroke-dasharray', function(d, i) {
    //   return d3
    //     .select(this)
    //     .node()
    //     .getTotalLength()
    // })
    // .style('stroke-dashoffset', function(d, i) {
    //   return d3
    //     .select(this)
    //     .node()
    //     .getTotalLength()
    // })
    // .transition()
    // .duration(this.CHART_DURATION)
    // .ease(d3.easeLinear)
    // .style('stroke-dashoffset', 0)
  }

  // 画折线图
  drawPolylineChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    let chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    // 定义折线画法
    const polylineFun = d3
      .line()
      .defined((d, i) => {
        if (attr.defined) {
          return attr.defined(d, i, that, attr);
        }
        // 控制是否绘制该点（return false不绘制）
        const xValue = d[that.xAxisKey];
        const dataValue = getDataValue(attr, d, i);
        return !(
          [undefined, null, '', 0].includes(xValue) ||
          isNaN(Number(xValue)) ||
          [undefined, null, ''].includes(dataValue) ||
          isNaN(Number(dataValue)) ||
          isNaN(that.yScales[chartName](Number(dataValue)))
        );
      })
      .x((d, i) => {
        if (d[that.xAxisKey] === 0) {
          console.log(i, d[that.xAxisKey], that.xScale(d[that.xAxisKey]));
        }
        return that.xScale(d[that.xAxisKey]);
      })
      .y((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
      })
      .curve(attr.curve || d3.curveBumpX);

    // 渐变色
    const linerGradientId = that.createLinearGradient(className, attr.color);

    chart.remove();
    chart = that.content.append('g').attr('class', className);

    that.polylineChartAnimation(
      chart
        .append('path')
        .attr('d', polylineFun(chartData))
        .attr('stroke', `url(#${linerGradientId})`)
        .attr('stroke-width', 1)
        .attr('fill', 'none')
    );
  }

  // 画点图
  drawPointChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    const chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }
    let r = (that.contentWidth / (chartData.length + 1)) * 0.2;
    r = r < 2 ? 2 : r > 6 ? 6 : r;
    // 点的公共属性设置
    const setPointAttr = (select) => {
      return select
        .attr('class', 'circle')
        .attr('cx', (d, i) => {
          return that.xScale(d[that.xAxisKey]);
        })
        .attr('cy', (d, i) => {
          return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
        })
        .attr('r', r)
        .attr('fill', (d, i) => {
          if (attr.fill && attr.fill instanceof Function) {
            return attr.fill(d, i);
          }
          return that.getFillColor(attr.color);
        });
    };

    const setPointListener = (select) => {
      // const duration = 100
      return select
        .on('mouseover', function (e) {
          setPointAttr(d3.select(this).transition().duration(100))
            .attr('r', r * 1.5)
            .attr('fill', that.getFillColor(attr.color, 0.4));
        })
        .on('mouseleave', function (e) {
          setPointAttr(d3.select(this).transition().duration(100));
          // .attr('r', r)
          // .attr('fill', that.getFillColor(attr.color))
        });
    };

    // 没有创建过该属性的图表
    chart.remove();
    const group = that.content.append('g').attr('class', className);

    setPointAttr(
      setPointListener(
        group
          .selectAll('.circle')
          .data(chartData)
          .enter()
          .append('circle')
          .filter((d, i) => {
            if (attr.filter) {
              return attr.filter(d, i, that, attr);
            }
            // 控制是否绘制该点（return false不绘制）
            return !(
              isNaN(getDataValue(attr, d, i)) ||
              isNaN(that.yScales[chartName](getDataValue(attr, d, i))) ||
              [undefined, ''].includes(getDataValue(attr, d, i))
            );
          })
      )
    );
  }

  // 画柱状图
  drawHistogramChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    const chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    let rectWidth = (that.contentWidth / (chartData.length + 1)) * 0.7;
    rectWidth = rectWidth > 60 ? 60 : rectWidth;
    // 柱的公共属性设置
    const setHistogramAttr = (select) => {
      return select
        .attr('class', 'rect')
        .attr('x', (d, i) => {
          return that.xScale(d[that.xAxisKey]) - rectWidth / 2;
        })
        .attr('y', (d, i) => {
          let height = 0;
          if (attr.y && attr.y instanceof Function) {
            height = that.contentHeight - attr.y(d, i, that, attr);
          } else {
            height = that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
          }
          if (height <= 0) {
            height = 1;
          }
          return that.contentHeight - height;
        })
        .attr('width', rectWidth)
        .attr('height', (d, i) => {
          let height = 0;
          if (attr.y && attr.y instanceof Function) {
            height = that.contentHeight - attr.y(d, i, that, attr);
          } else {
            height = that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
          }
          if (height <= 0) {
            height = 1;
          }
          return height;
        })
        .attr('fill', that.getFillColor(attr.color));
    };
    const setHistogramListener = (select) => {
      const duration = 100;
      return select
        .on('mouseover', function (e) {
          setHistogramAttr(d3.select(this).transition().duration(duration)).attr('fill', that.getFillColor(attr.color, 0.4));
        })
        .on('mouseleave', function (e) {
          setHistogramAttr(d3.select(this).transition().duration(duration)).attr('fill', that.getFillColor(attr.color));
        });
    };

    // 没有创建过该属性的图表
    chart.remove();
    const group = that.content.append('g').attr('class', className);

    setHistogramAttr(
      setHistogramListener(
        group
          .selectAll('.rect')
          .data(chartData)
          .enter()
          .append('rect')
          .filter(
            attr.filter ||
              ((d, i) => {
                // 控制是否绘制该点（return false不绘制）
                return !(
                  isNaN(getDataValue(attr, d, i)) ||
                  isNaN(that.yScales[chartName](getDataValue(attr, d, i))) ||
                  [undefined, ''].includes(getDataValue(attr, d, i))
                );
              })
          )
      )
    );
  }

  // 画多色面积图（无折线）
  drawMultiColorFillAreaChart(attr) {
    const chartName = attr.chartName;
    const getDataValue = this.getDataValue;
    const className = this.attrGroupClassName(attr);
    let chart = this.content.select(`.${className}`);
    let chartData = attr.data || this.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    // 定义面积画法
    const areaFun = (multiAreaIndex) =>
      d3
        .area()
        .defined((d, i) => {
          if (attr.defined) {
            return attr.defined(d, i, this, attr);
          }
          // 控制是否绘制该点（return false不绘制）
          const xValue = d[this.xAxisKey];
          const dataValue = getDataValue(attr, d, i, { multiAreaIndex });
          return !(
            [undefined, null, '', 0].includes(xValue) ||
            isNaN(Number(xValue)) ||
            [undefined, null, ''].includes(dataValue) ||
            isNaN(Number(dataValue)) ||
            isNaN(this.yScales[chartName](Number(dataValue)))
          );
        })
        .x((d, i) => {
          return this.xScale(d[this.xAxisKey]);
        })
        .y0((d, i) => {
          let result = this.contentHeight;
          if (attr.y0 && attr.y0 instanceof Function) {
            result = attr.y0(d, i, this, attr);
          } else {
            result = this.contentHeight - this.yScales[chartName](this.valueCorrect(attr, getDataValue(attr, d, i)));
          }
          if (this.contentHeight === result) {
            result = result - 2;
          }

          return result;
        })
        .y1((d, i) => {
          let result = this.contentHeight;
          if (attr.y1 && attr.y1 instanceof Function) {
            result = attr.y1(d, i, this, attr);
          } else {
            result = this.contentHeight;
          }
          return result;
        })
        .curve(attr.curve || d3.curveBumpX);

    // 没有创建过该属性的图表
    chart.remove();
    chart = this.content.insert('g', '*:nth-child(1)').attr('class', className);

    //绘制图例区域
    const legendArea = this.content
      .append('g')
      .attr('class', 'legend')
      .attr('id', `${className}-legend`)
      .attr('transform', `translate(${-(36 + this.margin.left - 40)},-36)`);

    for (const key in attr.multiAreaColors) {
      const item = attr.multiAreaColors[key];
      // console.log(`colors`, key, item);
      let hasChart = attr.gradeStyles ? attr.gradeStyles.includes(item.value) : false;
      if (item.nochart) hasChart = false;

      if (hasChart) {
        // 面积图渐变色
        const areaLinerGradientId0 = this.createLinearGradient(`${className}-area${key}`, item.colors);
        chart
          .append('path')
          .attr('class', `area${key}`)
          .attr('d', areaFun(item.value)(chartData))
          .attr('fill', `url(#${areaLinerGradientId0})`);

        if (!attr.isOverview) {
          // 设置每个图例的位置
          const legend = legendArea.append('g');
          // 添加图例的矩形色块
          legend
            .append('circle')
            .attr('cx', 0)
            .attr('cy', 6)
            .attr('r', 3)
            .style('fill', function (d, i) {
              return item.legend.color;
            });
          // 添加图例文字
          legend
            .append('text')
            .attr('x', 10)
            .attr('y', 6)
            .style('fill', function (d, i) {
              return '#fff';
            })
            .style('font-size', '12px')
            .attr('dy', '.35em')
            .text(function (d, i) {
              return item.legend.label;
            });
        }
      }
    }
    // 处理图例位置
    const rectGroup = document.getElementById(`${className}-legend`);
    const rects = rectGroup.children;
    let x = 0;
    for (let i = 0; i < rects.length; i++) {
      const rect = rects[i];
      const bbox = rect.getBBox();
      rect.setAttribute('transform', `translate(${x},0)`);
      x += bbox.width + 10;
    }
  }

  // 画单色面积图（无折线）
  drawFillAreaChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    let chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    // 定义面积画法
    const areaFun = d3
      .area()
      .defined((d, i) => {
        if (attr.defined) {
          return attr.defined(d, i, that, attr);
        }
        // 控制是否绘制该点（return false不绘制）
        const xValue = d[that.xAxisKey];
        const dataValue = getDataValue(attr, d, i);
        return !(
          [undefined, null, '', 0].includes(xValue) ||
          isNaN(Number(xValue)) ||
          [undefined, null, ''].includes(dataValue) ||
          isNaN(Number(dataValue)) ||
          isNaN(that.yScales[chartName](Number(dataValue)))
        );
      })
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y0((d, i) => {
        let result = that.contentHeight;
        if (attr.y0 && attr.y0 instanceof Function) {
          result = attr.y0(d, i, that, attr);
        } else {
          result = that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
        }
        if (that.contentHeight === result) {
          result = result - 2;
        }

        return result;
      })
      .y1((d, i) => {
        let result = that.contentHeight;
        if (attr.y1 && attr.y1 instanceof Function) {
          result = attr.y1(d, i, that, attr);
        } else {
          result = that.contentHeight;
        }

        return result;
      })
      .curve(attr.curve || d3.curveBumpX);

    // 面积图渐变色
    const areaLinerGradientId = that.createLinearGradient(`${className}-area`, attr.areaColor || attr.color);

    // 没有创建过该属性的图表
    chart.remove();
    chart = that.content.insert('g', '*:nth-child(1)').attr('class', className);

    chart.append('path').attr('class', 'area').attr('d', areaFun(chartData)).attr('fill', `url(#${areaLinerGradientId})`);
  }

  // 画面积图
  drawAreaChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const getDataValue = that.getDataValue;
    const className = that.attrGroupClassName(attr);
    let chart = that.content.select(`.${className}`);
    let chartData = attr.data || that.chartData;

    if (attr.filter && attr.filter instanceof Function) {
      chartData = chartData.filter((d, i) => {
        return attr.filter(d, i, attr);
      });
    }

    // 定义面积画法
    const areaFun = d3
      .area()
      .defined((d, i) => {
        if (attr.defined) {
          return attr.defined(d, i, that, attr);
        }
        // 控制是否绘制该点（return false不绘制）
        const xValue = d[that.xAxisKey];
        const dataValue = getDataValue(attr, d, i);
        return !(
          [undefined, null, '', 0].includes(xValue) ||
          isNaN(Number(xValue)) ||
          [undefined, null, ''].includes(dataValue) ||
          isNaN(Number(dataValue)) ||
          isNaN(that.yScales[chartName](Number(dataValue)))
        );
      })
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y0((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
      })
      .y1((d, i) => {
        return that.contentHeight;
      })
      .curve(attr.curve || d3.curveBumpX);

    // 定义折线画法
    const polylineFun = d3
      .line()
      .defined((d, i) => {
        if (attr.defined) {
          return attr.defined(d, i, that, attr);
        }
        // 控制是否绘制该点（return false不绘制）
        const xValue = d[that.xAxisKey];
        const dataValue = getDataValue(attr, d, i);
        return !(
          [undefined, null, '', 0].includes(xValue) ||
          isNaN(Number(xValue)) ||
          [undefined, null, ''].includes(dataValue) ||
          isNaN(Number(dataValue)) ||
          isNaN(that.yScales[chartName](Number(dataValue)))
        );
      })
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, getDataValue(attr, d, i)));
      })
      .curve(attr.curve || d3.curveBumpX);

    // 面积图渐变色
    const areaLinerGradientId = that.createLinearGradient(`${className}-area`, that.colorStrToGradientArray(attr.areaColor || attr.color));
    const linerGradientId = that.createLinearGradient(`${className}-line`, attr.color);

    // 没有创建过该属性的图表
    chart.remove();
    chart = that.content.insert('g', '*:nth-child(1)').attr('class', className);

    chart.append('path').attr('class', 'area').attr('d', areaFun(chartData)).attr('fill', `url(#${areaLinerGradientId})`);

    that.polylineChartAnimation(
      chart
        .append('path')
        .attr('class', 'line')
        .attr('d', polylineFun(chartData))
        .attr('stroke', `url(#${linerGradientId})`)
        .attr('stroke-width', 1)
        .attr('fill', 'none')
    );
  }

  // 画图表
  drawChart(attr) {
    switch (attr.chartType) {
      case CHART_TYPE_POLYLINE:
        this.drawPolylineChart(attr);
        break;
      case CHART_TYPE_POINT:
        this.drawPointChart(attr);
        break;
      case CHART_TYPE_HISTOGRAM:
        this.drawHistogramChart(attr);
        break;
      case CHART_TYPE_FILL_AREA:
        this.drawFillAreaChart(attr);
        break;
      case CHART_TYPE_MULTI_COLOR_FILL_AREA:
        this.drawMultiColorFillAreaChart(attr);
        break;
      case CHART_TYPE_AREA:
        this.drawAreaChart(attr);
        break;
    }
  }

  // 设置需要读取的属性
  setShowingAttributes(attributes = []) {
    this.yScales = {};
    this.showingAttributes = attributes;
    this.drawYScale(attributes);

    attributes
      .sort((a, b) => {
        return a.createTime - b.createTime;
        // return b.createTime > a.createTime ? -1 : 1
      })
      .forEach((attr) => {
        // 画图
        this.drawChart(attr);
        if (attr.chartName === 'Pace' && attr.chartType === 'polyline') {
          this.drawTestPaceZoneChart(attr);
        }
      });
  }

  // 获取文本元素大小
  getTextEleSize(text, fontSize = '12') {
    const ele = document.createElement('text');
    ele.style.fontSize = `${fontSize}px`;
    ele.textContent = text;
    document.body.append(ele);

    const rectInfo = ele.getBoundingClientRect();
    document.body.removeChild(ele);
    return {
      width: rectInfo.width,
      height: rectInfo.height
    };
  }

  // 生成x轴Tooltip
  drawXTooltip(text, x) {
    const that = this;
    const duration = 100;
    const fontSize = 10;
    const textEleSize = that.getTextEleSize(text, fontSize);
    const gap = that.xTickLineSize;
    const topBottomPadding = 5;
    const leftRightPadding = 15;
    const rectWidth = textEleSize.width + leftRightPadding * 2;
    const rectHeight = textEleSize.height + topBottomPadding * 2;
    const crossHair = that.svg.select('.cross-hair');
    const xTooltip = crossHair.select('.x-tooltip');
    if (that.isEmptySelect(xTooltip)) {
      xTooltip.remove();
      crossHair
        .append('rect')
        .attr('class', 'x-tooltip')
        .attr('x', x - rectWidth / 2)
        .attr('y', that.contentHeight + gap)
        .attr('width', rectWidth)
        .attr('height', rectHeight)
        .attr('rx', 6)
        .attr('ry', 6)
        .attr('fill', that.getFillColor(that.themeStyle.xTooltipBg));
    } else {
      xTooltip
        .transition()
        .duration(duration)
        .attr('class', 'x-tooltip')
        .attr('x', x - rectWidth / 2)
        .attr('width', rectWidth)
        .attr('height', rectHeight);
    }

    const xTooltipText = crossHair.select('.x-tooltip-text');
    if (that.isEmptySelect(xTooltipText)) {
      xTooltipText.remove();
      crossHair
        .append('text')
        .attr('class', 'x-tooltip-text')
        .attr('x', x)
        .attr('y', that.contentHeight + gap + topBottomPadding)
        .attr('fill', that.themeStyle.xTooltipColor)
        .attr('font-size', `${fontSize}px`)
        .attr('dominant-baseline', 'text-before-edge')
        .attr('text-anchor', 'middle')
        .text(text);
    } else {
      xTooltipText.transition().duration(duration).attr('x', x).text(text);
    }
  }

  // 绘制属性Tooltip
  drawAttrTooltip(x, y, title = '', nearlyData = {}) {
    const that = this;
    const attributeTooltip = that.el.getElementsByClassName('attribute-tooltip')[0];

    if (!attributeTooltip) {
      that.createAttrTooltip(title);
    } else {
      const offsetWidth = attributeTooltip.offsetWidth;
      let translateX = 60;
      let translateY = -10;
      if (that.contentWidth - x <= offsetWidth + 60) {
        translateX = -offsetWidth - 60;
      }

      translateX = that.margin.left + x + translateX;
      translateY = that.margin.top + y + translateY;

      attributeTooltip.style.opacity = '1';
      attributeTooltip.style.transform = `translate(${translateX}px, ${translateY}px)`;
    }
    that.updateAttrTooltip(title, nearlyData, that.showingAttributes);
  }

  // 删除属性Tooltip
  removeAttrTooltip() {
    const that = this;
    const attributeTooltip = that.el.getElementsByClassName('attribute-tooltip');
    if (that.el && attributeTooltip && attributeTooltip[0]) {
      attributeTooltip[0].remove();
    }
  }

  // 创建属性Tooltip
  createAttrTooltip(title = '') {
    const that = this;
    const tooltip = document.createElement('div');
    tooltip.className = 'attribute-tooltip';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.position = 'absolute';
    tooltip.style.padding = '8px 15px 10px 15px';
    tooltip.style.left = '0';
    tooltip.style.top = '0';
    tooltip.style.borderRadius = '2px';
    tooltip.style.outline = 'none';
    tooltip.style.boxShadow = '2px 2px 6px 0 rgba(0, 0, 0, 0.58)';
    tooltip.style.backgroundColor = that.themeStyle.attrTooltipBg;
    tooltip.style.transform = `translate(0, 0)`;
    tooltip.style.opacity = '0';
    tooltip.style.zIndex = '113';

    const titleEl = document.createElement('div');
    titleEl.className = 'title';
    titleEl.textContent = title;
    titleEl.style.fontSize = '20px';
    titleEl.style.color = that.themeStyle.attrTooltipTitleColor;
    tooltip.append(titleEl);

    const attributes = document.createElement('div');
    attributes.className = 'attributes';
    tooltip.append(attributes);

    that.el.append(tooltip);
  }

  // 更新属性Tooltip数据
  updateAttrTooltip(title = '', nearlyData = {}, attributes = []) {
    const that = this;
    const getDataValue = that.getDataValue;
    const attributeTooltip = that.el.getElementsByClassName('attribute-tooltip')[0];
    const titleEl = attributeTooltip.getElementsByClassName('title')[0];
    titleEl.textContent = title;

    const attributesEl = attributeTooltip.getElementsByClassName('attributes')[0];

    attributesEl.innerHTML = '';

    const i = nearlyData.index;
    const d = nearlyData.data;

    // 遍历需要显示的属性并创建对应的显示元素
    attributes.forEach((attr) => {
      if (attr.attrTooltipDefined && !attr.attrTooltipDefined(d, i)) {
        return;
      }
      const parentRow = document.createElement('div');
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.flexWrap = 'nowrap';
      // row.style.height = '21px'
      row.style.alignItems = 'center';
      row.style.boxSizing = 'border-box';
      row.style.padding = '5px 0';

      // 颜色块
      const color = document.createElement('span');
      color.style.flexGrow = '0';
      color.style.flexShrink = '0';
      color.style.width = '6px';
      color.style.height = '15px';
      color.style.borderRadius = '3px';

      if (Array.isArray(attr.color)) {
        const stops = [];
        attr.color.forEach((item) => {
          stops.push(`${item.color} ${item.offset}`);
        });
        color.style.background = `linear-gradient(to bottom, ${stops.join(', ')})`;
      } else {
        color.style.background = attr.color;
      }
      row.append(color);

      // 值文本
      const text = document.createElement('span');
      const textValue = attr.tickText ? attr.tickText(getDataValue(attr, d, i), d) : getDataValue(attr, d, i);

      text.style.flexGrow = '1';
      text.style.flexShrink = '1';
      text.style.lineHeight = '21px';
      text.style.fontSize = '16px';
      text.style.color = that.themeStyle.attrItemTextColor;
      text.style.marginLeft = '5px';
      text.textContent = `${attr.text}：${textValue}${attr.unit || ''}`;
      row.append(color);
      row.append(text);
      parentRow.append(row);
      const recommandRow = this.getRecommandEl(attr, i, d);

      parentRow.append(recommandRow);
      attributesEl.append(parentRow);
    });
  }

  getRecommandEl(attr, i, d) {
    // 体能测试推荐范围
    const getDataValue = this.getDataValue;
    const recommandLabel = this.recommandLabel;

    const _attr = _.cloneDeep(attr);
    _attr.valueKey = 'testMaxPace';
    const recommandMaxValue = getDataValue(_attr, d, i);
    const recommandTextMaxValue = attr.tickText ? attr.tickText(recommandMaxValue, d) : getDataValue(_attr, d, i);
    _attr.valueKey = 'testMinPace';
    const recommandMinValue = getDataValue(_attr, d, i);
    const recommandTextMinValue = attr.tickText ? attr.tickText(recommandMinValue, d) : getDataValue(_attr, d, i);
    if (attr.chartName !== 'Pace' || attr.chartType !== 'polyline' || !recommandMaxValue || !recommandTextMaxValue) {
      return '';
    }

    const recommandRow = document.createElement('div');
    recommandRow.style.display = 'flex';
    recommandRow.style.flexWrap = 'nowrap';
    // row.style.height = '21px'
    recommandRow.style.alignItems = 'center';
    recommandRow.style.boxSizing = 'border-box';
    recommandRow.style.padding = '5px 0';

    const recommandText = document.createElement('span');

    recommandText.style.flexGrow = '1';
    recommandText.style.flexShrink = '1';
    recommandText.style.lineHeight = '21px';
    recommandText.style.fontSize = '16px';
    recommandText.style.color = this.themeStyle.attrItemTextColor;
    recommandText.style.marginLeft = '5px';
    recommandText.textContent = `${recommandLabel}：${recommandTextMaxValue}-${recommandTextMinValue}${attr.unit || ''}`;
    const recommandColor = document.createElement('span');
    recommandColor.style.flexGrow = '0';
    recommandColor.style.flexShrink = '0';
    recommandColor.style.width = '6px';
    recommandColor.style.height = '15px';
    recommandColor.style.borderRadius = '3px';
    recommandColor.style.opacity = '0.2';
    if (Array.isArray(attr.color)) {
      const stops = [];
      attr.color.forEach((item) => {
        stops.push(`${item.color} ${item.offset}`);
      });
      recommandColor.style.background = `linear-gradient(to bottom, ${stops.join(', ')})`;
    } else {
      recommandColor.style.background = attr.color;
    }
    recommandRow.append(recommandColor);
    recommandRow.append(recommandText);
    return recommandRow;
  }

  // 获取数据中最接近的x的值
  getSimilarXValue(value) {
    const that = this;
    return unitHelper.getNearlyDataByBinarySearch({
      originalData: that.chartData,
      key: that.xAxisKey,
      value
    });
  }

  // 绘制十字线的横竖线
  drawCrossHairLine(x, y) {
    const that = this;

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    let crossHair = that.svg.select('.cross-hair');
    if (that.isEmptySelect(crossHair)) {
      crossHair = that.svg.append('g').attr('class', 'cross-hair').attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    }
    const horizontalLine = crossHair.select('.horizontal-line');
    const verticalLine = crossHair.select('.vertical-line');

    // 横线
    horizontalLine.remove();
    if (that.showCrossHairHorizontalLine) {
      crossHair
        .append('path')
        .attr('class', 'horizontal-line')
        .attr(
          'd',
          lineFun([
            { x: 0, y },
            { x: that.contentWidth, y }
          ])
        )
        .attr('pointer-events', 'none')
        .attr('fill', 'none')
        .attr('stroke-width', 1)
        .attr('stroke', that.themeStyle.crossHairHorizontalLineColor)
        .attr('stroke-dasharray', that.STROKE_DASHARRAY);
    }

    // 竖线
    if (that.isEmptySelect(verticalLine)) {
      verticalLine.remove();
      if (that.showCrossHairVerticalLine) {
        crossHair
          .append('path')
          .attr('class', 'vertical-line')
          .attr(
            'd',
            lineFun([
              { x: 0, y: 0 },
              { x: 0, y: that.contentHeight }
            ])
          )
          .attr('transform', `translate(${x}, 0)`)
          .attr('pointer-events', 'none')
          .attr('fill', 'none')
          .attr('stroke-width', 1)
          .attr('stroke', that.themeStyle.crossHairVerticalLineColor)
          .attr('stroke-dasharray', that.STROKE_DASHARRAY);
      }
    } else {
      verticalLine.attr('transform', `translate(${x}, 0)`);
    }
  }

  // 十字线
  drawCrossHair(e) {
    const that = this;
    const clientRect = that.el.getBoundingClientRect();
    // const clientRect = that.svg.node().getBoundingClientRect()
    let x = e.clientX - clientRect.left - that.margin.left;
    let y = e.clientY - clientRect.top - that.margin.top;

    if (x < 0) {
      x = 0;
    } else if (x > that.contentWidth) {
      x = that.contentWidth;
    }

    if (y < 0) {
      y = 0;
    } else if (y > that.contentHeight) {
      y = that.contentHeight;
    }

    // 使用比例尺反向获取该坐标对应的值，并返回与该值最接近的数据值
    const nearlyData = that.getSimilarXValue(that.getValueFromXScale(x));
    if (!nearlyData) {
      return;
    }

    const xm = nearlyData.data[that.xAxisKey];
    if (!xm) {
      return;
    }

    // 获取对应改x坐标的数据
    const xValueInfo = nearlyData.data;
    // 返回x坐标及其处理后的文本
    const xTickInfo = that.xTickInfo(xm);

    if (!xValueInfo) {
      return;
    }
    x = that.xScale(xValueInfo[that.xAxisKey]);

    if (that.crossHairCallback) {
      that.crossHairCallback(e, xValueInfo);
    }

    // 绘制十字线的横竖线
    that.drawCrossHairLine(x, y);
    // // 绘制x轴Tooltip
    // that.drawXTooltip(xTickInfo.text, x)
    // 绘制属性Tooltip
    if (that.showAttrTooltip) {
      that.drawAttrTooltip(x, y, xTickInfo.text, nearlyData);
    }
  }

  // 清除十字线
  deleteCrossHair() {
    const that = this;
    const crossHair = that.svg.select('.cross-hair');
    crossHair.remove();
    // 删除Tooltip
    if (that.el && that.el.getElementsByClassName('attribute-tooltip') && that.el.getElementsByClassName('attribute-tooltip')[0]) {
      that.el.getElementsByClassName('attribute-tooltip')[0].remove();
    }
  }

  // 绘制遮罩层
  createMaskLayer(lockedZone, openZone) {
    const that = this;
    const className = `mask-layer`;
    let maskLayer = that.svg.select(`.${className}`);

    const areaFun = d3
      .area()
      .x((d, i) => {
        return that.xScale(d);
      })
      .y0((d, i) => {
        return that.contentHeight;
      })
      .y1((d, i) => {
        return 0;
      });

    maskLayer.remove();
    maskLayer = that.svg.append('g').attr('class', className).attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);

    lockedZone.zone.forEach((item, index) => {
      maskLayer
        .append('path')
        .attr('class', 'locked-zone')
        .attr('d', areaFun([item.start, item.end]))
        .attr('fill', that.getFillColor(`${lockedZone.bg}`));
    });

    openZone.zone.forEach((item, index) => {
      maskLayer
        .append('path')
        .attr('class', 'open-zone')
        .attr('d', areaFun([item.start, item.end]))
        .attr('fill', that.getFillColor(`${openZone.bg}`));
    });

    const rectWidth = 2;
    const textSize = 12;
    openZone.zone.forEach((item, index) => {
      maskLayer
        .append('rect')
        .attr('class', 'open-zone-left-edge')
        .attr('x', (d, i) => {
          return that.xScale(item.start) - rectWidth / 2;
        })
        .attr('y', (d, i) => {
          return 0;
        })
        .attr('width', rectWidth)
        .attr('height', (d, i) => {
          return that.contentHeight;
        })
        .attr('fill', that.getFillColor(`${openZone.edgeColor}`));

      // 左边缘文字
      maskLayer
        .append('text')
        .attr('class', 'open-zone-left-edge')
        .attr('x', that.xScale(item.start))
        .attr('y', -35)
        .attr('fill', that.themeStyle.openZoneLeftEdgeColor)
        .attr('font-size', `${textSize}px`)
        .attr('dominant-baseline', 'text-before-edge')
        .attr('text-anchor', 'middle')
        .text(item.startText);

      maskLayer
        .append('rect')
        .attr('class', 'open-zone-right-edge')
        .attr('x', (d, i) => {
          return that.xScale(item.end) - rectWidth / 2;
        })
        .attr('y', (d, i) => {
          return 0;
        })
        .attr('width', rectWidth)
        .attr('height', (d, i) => {
          return that.contentHeight;
        })
        .attr('fill', that.getFillColor(`${openZone.edgeColor}`));

      // 右边缘文字
      maskLayer
        .append('text')
        .attr('class', 'open-zone-right-edge')
        .attr('x', that.xScale(item.end))
        .attr('y', -35)
        .attr('fill', that.themeStyle.openZoneRightEdgeColor)
        .attr('font-size', `${textSize}px`)
        .attr('dominant-baseline', 'text-before-edge')
        .attr('text-anchor', 'middle')
        .text(item.endText);
    });
  }

  // 绘制框选颜色块
  createSelectionZone(options) {
    const that = this;
    const rectWidth = 1;
    const domain = that.xScale.domain();
    const className = `selection-zone`;
    let maskLayer = that.svg.select(`.${className}`);

    const areaFun = d3
      .area()
      .x((d, i) => {
        return that.xScale(d);
      })
      .y0((d, i) => {
        return that.contentHeight;
      })
      .y1((d, i) => {
        return 0;
      });

    if (that.isEmptySelect(maskLayer)) {
      maskLayer = that.svg.append('g').attr('class', className).attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    } else {
      maskLayer
        // .transition()
        // .duration(that.CHART_DURATION)
        .attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    }

    // 选中区的左侧遮罩
    if (domain[0] !== options.start) {
      const area = maskLayer.select('.left-area');
      const line = maskLayer.select('.left-line');

      if (that.isEmptySelect(area)) {
        maskLayer
          .append('path')
          .attr('class', 'left-area')
          .attr('d', areaFun([domain[0], options.start]))
          .attr('fill', that.getFillColor(`${options.color}`));
      } else {
        area
          // .transition()
          // .duration(that.CHART_DURATION)
          .attr('d', areaFun([domain[0], options.start]))
          .attr('fill', that.getFillColor(`${options.color}`));
      }

      if (that.isEmptySelect(line)) {
        maskLayer
          .append('rect')
          .attr('class', 'left-line')
          .attr('x', (d, i) => {
            return that.xScale(options.start) - rectWidth / 2;
          })
          .attr('y', (d, i) => {
            return 0;
          })
          .attr('width', rectWidth)
          .attr('height', (d, i) => {
            return that.contentHeight;
          })
          .attr('fill', that.getFillColor(`${options.edgeColor}`));
      } else {
        line
          // .transition()
          // .duration(that.CHART_DURATION)
          .attr('x', (d, i) => {
            return that.xScale(options.start) - rectWidth / 2;
          })
          .attr('y', (d, i) => {
            return 0;
          })
          .attr('width', rectWidth)
          .attr('height', (d, i) => {
            return that.contentHeight;
          });
      }
    } else {
      maskLayer.select('.left-area').remove();
      maskLayer.select('.left-line').remove();
    }

    // 选中区的右侧遮罩
    if (domain[1] !== options.end) {
      const area = maskLayer.select('.right-area');
      const line = maskLayer.select('.right-line');

      if (that.isEmptySelect(area)) {
        maskLayer
          .append('path')
          .attr('class', 'right-area')
          .attr('d', areaFun([options.end, domain[1]]))
          .attr('fill', that.getFillColor(`${options.color}`));
      } else {
        area
          // .transition()
          // .duration(that.CHART_DURATION)
          .attr('d', areaFun([options.end, domain[1]]))
          .attr('fill', that.getFillColor(`${options.color}`));
      }

      if (that.isEmptySelect(line)) {
        maskLayer
          .append('rect')
          .attr('class', 'right-line')
          .attr('x', (d, i) => {
            return that.xScale(options.end) - rectWidth / 2;
          })
          .attr('y', (d, i) => {
            return 0;
          })
          .attr('width', rectWidth)
          .attr('height', (d, i) => {
            return that.contentHeight;
          })
          .attr('fill', that.getFillColor(`${options.edgeColor}`));
      } else {
        line
          // .transition()
          // .duration(that.CHART_DURATION)
          .attr('x', (d, i) => {
            return that.xScale(options.end) - rectWidth / 2;
          })
          .attr('y', (d, i) => {
            return 0;
          })
          .attr('width', rectWidth)
          .attr('height', (d, i) => {
            return that.contentHeight;
          });
      }
    } else {
      maskLayer.select('.right-area').remove();
      maskLayer.select('.right-line').remove();
    }
  }

  // 删除框选颜色块
  deleteSelectionZone() {
    const that = this;
    const className = `selection-zone`;
    that.svg.select(`.${className}`).remove();
  }
}
